import React, { useState, useEffect, useCallback } from 'react';
import { trackReactionsApi } from '../../api/trackReactions';
import HrnbLoader from '../controls/HrnbLoader';
import '../../css/components/TrackReactions.css';

const QUICK_EMOJIS = ['🔥', '👏', '💯', '🤯', '💪', '🎯'];

/**
 * Компонент emoji-реакций на трек в раунде баттла.
 *
 * @component
 * @param {Object} props
 * @param {string} props.battleId — UUID баттла
 * @param {string} props.roundId — UUID раунда
 * @param {string} props.trackId — UUID трека
 * @param {boolean} [props.compact=false] — Компактный режим (только счётчики)
 */
export default function TrackReactions({ battleId, roundId, trackId, compact = false }) {
    const [reactions, setReactions] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(null);
    const [error, setError] = useState(null);
    const [showPicker, setShowPicker] = useState(false);

    /** Загрузка реакций и статистики */
    const load = useCallback(async () => {
        if (!battleId || !roundId || !trackId) return;
        try {
            setLoading(true);
            const [reactionsData, statsData] = await Promise.all([
                trackReactionsApi.list(battleId, roundId, trackId),
                trackReactionsApi.getStats(battleId, roundId, trackId),
            ]);
            setReactions(reactionsData || []);
            setStats(statsData);
            setError(null);
        } catch (err) {
            console.error('[TrackReactions] Failed to load reactions:', err.message);
            setError('Не удалось загрузить реакции');
        } finally {
            setLoading(false);
        }
    }, [battleId, roundId, trackId]);

    useEffect(() => {
        load();
    }, [load]);

    /** Добавить реакцию */
    const handleAdd = async (emoji) => {
        try {
            setActionLoading(emoji);
            await trackReactionsApi.add(battleId, roundId, trackId, { emoji });
            await load();
            setShowPicker(false);
        } catch (err) {
            console.error('[TrackReactions] Failed to add reaction:', err.message);
            setError('Не удалось добавить реакцию');
        } finally {
            setActionLoading(null);
        }
    };

    /** Удалить конкретную реакцию */
    const handleRemove = async (emoji) => {
        try {
            setActionLoading(emoji);
            await trackReactionsApi.removeOne(battleId, roundId, trackId, emoji);
            await load();
        } catch (err) {
            console.error('[TrackReactions] Failed to remove reaction:', err.message);
            setError('Не удалось удалить реакцию');
        } finally {
            setActionLoading(null);
        }
    };

    if (loading) {
        return <HrnbLoader size="sm" />;
    }

    const byEmoji = stats?.byEmoji || {};
    const emojiList = Object.keys(byEmoji);

    if (compact) {
        return (
            <div className="track-reactions track-reactions--compact">
                {emojiList.length > 0 ? (
                    emojiList.map((emoji) => (
                        <span key={emoji} className="reaction-chip">
                            {emoji} {byEmoji[emoji]}
                        </span>
                    ))
                ) : (
                    <span className="track-reactions__empty">Нет реакций</span>
                )}
            </div>
        );
    }

    return (
        <div className="track-reactions">
            {error && (
                <div className="track-reactions__error">
                    <small>{error}</small>
                </div>
            )}

            {/* Существующие реакции */}
            <div className="track-reactions__list">
                {emojiList.map((emoji) => (
                    <button
                        key={emoji}
                        type="button"
                        className="reaction-chip reaction-chip--interactive"
                        onClick={() => handleRemove(emoji)}
                        disabled={actionLoading === emoji}
                        title="Нажмите, чтобы убрать свою реакцию"
                    >
                        {emoji} {byEmoji[emoji]}
                    </button>
                ))}

                {/* Кнопка добавления */}
                <button
                    type="button"
                    className="reaction-add-btn"
                    onClick={() => setShowPicker(!showPicker)}
                    disabled={actionLoading !== null}
                    title="Добавить реакцию"
                >
                    {showPicker ? '✕' : '+ 😊'}
                </button>
            </div>

            {/* Пикер эмодзи */}
            {showPicker && (
                <div className="track-reactions__picker">
                    {QUICK_EMOJIS.map((emoji) => (
                        <button
                            key={emoji}
                            type="button"
                            className="reaction-picker-item"
                            onClick={() => handleAdd(emoji)}
                            disabled={actionLoading !== null}
                        >
                            {actionLoading === emoji ? '⏳' : emoji}
                        </button>
                    ))}
                </div>
            )}

            {stats && (
                <div className="track-reactions__total">
                    Всего: {stats.totalCount || 0}
                </div>
            )}
        </div>
    );
}
